// Calculateur de boissons : moments de la journée, boissons servies, verres par
// personne, et bouteilles nécessaires. Fonctions pures (partagées par l'outil et
// par les tableaux de référence de la page, pour des chiffres identiques).

export type DrinkKey = 'rouge' | 'blanc' | 'rose' | 'champagne';
export type MomentKey = 'cocktail' | 'diner' | 'brunch';

export const DRINKS: Array<{ key: DrinkKey; label: string; color: string }> = [
  { key: 'rouge', label: 'Vin rouge', color: '#8E2F3C' },
  { key: 'blanc', label: 'Vin blanc', color: '#D9C36A' },
  { key: 'rose', label: 'Rosé', color: '#E8A0A8' },
  { key: 'champagne', label: 'Champagne', color: '#C9A227' },
];

export const MOMENTS: Array<{ key: MomentKey; label: string; hint: string }> = [
  { key: 'cocktail', label: 'Vin d’honneur', hint: 'Cocktail après la cérémonie, 1 h 30 à 2 h' },
  { key: 'diner', label: 'Dîner', hint: 'Repas assis, dessert et soirée compris' },
  { key: 'brunch', label: 'Brunch', hint: 'Le lendemain midi' },
];

// Verres proposés par personne : 0 = boisson non servie à ce moment par défaut
export const DEFAULT_GLASSES: Record<MomentKey, Record<DrinkKey, number>> = {
  cocktail: { rouge: 0, blanc: 1, rose: 1, champagne: 2 },
  diner: { rouge: 2, blanc: 1, rose: 0, champagne: 1 },
  brunch: { rouge: 0, blanc: 1, rose: 1, champagne: 0 },
};

// Bouteille de 75 cl : 6 verres de vin (12,5 cl) ou 6 flûtes de champagne
export const GLASSES_PER_BOTTLE = 6;
export const DEFAULT_MARGIN = 10; // %

export interface MomentSettings {
  enabled: boolean;
  guests: number | null; // null : nombre d'invités général
  served: Record<DrinkKey, boolean>;
  glasses: Record<DrinkKey, number>;
}

export interface DrinksSettings {
  guests: number;
  margin: number; // %
  drinks: Record<DrinkKey, boolean>; // boissons proposées du tout (« pas de rosé »)
  moments: Record<MomentKey, MomentSettings>;
}

export function defaultSettings(guests = 80): DrinksSettings {
  const moment = (key: MomentKey): MomentSettings => {
    const g = DEFAULT_GLASSES[key];
    return {
      enabled: true,
      guests: null,
      served: { rouge: g.rouge > 0, blanc: g.blanc > 0, rose: g.rose > 0, champagne: g.champagne > 0 },
      // Verres proposés même pour une boisson non servie, au cas où on l'ajoute
      glasses: {
        rouge: g.rouge || 1,
        blanc: g.blanc || 1,
        rose: g.rose || 1,
        champagne: g.champagne || 1,
      },
    };
  };
  return {
    guests,
    margin: DEFAULT_MARGIN,
    drinks: { rouge: true, blanc: true, rose: true, champagne: true },
    moments: { cocktail: moment('cocktail'), diner: moment('diner'), brunch: moment('brunch') },
  };
}

export const bottlesFor = (guests: number, glasses: number, margin: number) =>
  guests > 0 && glasses > 0 ? Math.ceil(((guests * glasses) / GLASSES_PER_BOTTLE) * (1 + margin / 100)) : 0;

export interface MomentResult {
  key: MomentKey;
  guests: number;
  bottles: Record<DrinkKey, number>; // 0 si non servie
}

export interface DrinksResult {
  moments: MomentResult[]; // moments activés
  totals: Record<DrinkKey, number>;
  total: number;
}

const zero = (): Record<DrinkKey, number> => ({ rouge: 0, blanc: 0, rose: 0, champagne: 0 });

export function computeDrinks(s: DrinksSettings): DrinksResult {
  const totals = zero();
  const moments = MOMENTS.filter((m) => s.moments[m.key].enabled).map(({ key }) => {
    const m = s.moments[key];
    const guests = m.guests ?? s.guests;
    const bottles = zero();
    for (const { key: d } of DRINKS) {
      if (!s.drinks[d] || !m.served[d]) continue;
      bottles[d] = bottlesFor(guests, m.glasses[d], s.margin);
      totals[d] += bottles[d];
    }
    return { key, guests, bottles };
  });
  return { moments, totals, total: DRINKS.reduce((n, d) => n + totals[d.key], 0) };
}
